import {getUrlParams} from "../shared/helpers/parseUrl";

type TResponseType =
  | "balanceChanged"
  | "stopAutoSpin"
  | "unfreeze"
  | "togglePaytable"
  | "toggleHelp"
  | "mute"
  | "toggleAbout";

enum LIGHT_AND_WONDER_RECEIVE_MESSAGE_TYPE {
  BALANCE_HAS_CHANGED = "com.lnw.gcm.balancesHasChanged",
  OPTION_HAS_CHANGED = "com.lnw.gcm.optionHasChanged",
  STOP_AUTOPLAY = "com.lnw.gcm.stopAutoPlay",
  RESUME = "com.lnw.gcm.resume",
}

enum LIGHT_AND_WONDER_EVENT {
  INIT = "com.lnw.gcm.initialiseChannel",
  SET_GAME_CONFIG = "com.lnw.gcm.setGameConfig",
  PROGRESS_UPDATE = "com.lnw.gcm.loadProgressUpdate",
  GAME_READY = "com.lnw.gcm.gameReady",
  REGISTER_OPTION = "com.lnw.gcm.regOption",
  BALANCE_UPDATE = "com.lnw.gcm.balancesUpdate",
  PAID_UPDATE = "com.lnw.gcm.paidUpdate",
  STAKE_UPDATE = "com.lnw.gcm.stakeUpdate",
  OPTION_HAS_CHANGED = "com.lnw.gcm.optionHasChanged",
  ROUND_START = "com.lnw.gcm.gameAnimationStart",
  ROUND_END = "com.lnw.gcm.gameAnimationComplete",
  HANDLE_ERROR = "com.lnw.gcm.handleError",
  HANDLE_MESSAGE_TRIGGER = 'com.lnw.gcm.handleMessageTrigger',
}

enum LIGHT_AND_WONDER_ERROR_CATEGORY {
  NON_RECOVERABLE_ERROR = "NON_RECOVERABLE_ERROR",
  CRITICAL = "CRITICAL",
  INSUFFICIENT_FUNDS = "INSUFFICIENT_FUNDS",
}

enum LIGHT_AND_WONDER_ERROR_SEVERITY {
  WARNING = "WARNING",
  INFO = "INFO",
  ERROR = "ERROR",
}

class LightAndWonderClient {
  private observers: { [key: string]: ((...args: any) => void)[] } = {};

  //NEW STUFF
  private messagingChannel: MessageChannel;
  private messagePorts: {
    port1: MessagePort;
    port2: MessagePort;
  };

  constructor() {
    this.messagingChannel = new MessageChannel();
    this.messagePorts = {
      port1: this.messagingChannel.port1,
      port2: this.messagingChannel.port2,
    };

    window.parent.postMessage(this.prepareGcmMsg(LIGHT_AND_WONDER_EVENT.INIT), "*", [
      this.messagePorts.port2,
    ]);
    this.messagePorts.port1.onmessage = this.onGcmMsgReceived.bind(this);

    //SEND GCM GAME CONFIG SETTINGS
    this.messagePorts.port1.postMessage(
      this.prepareGcmMsg(LIGHT_AND_WONDER_EVENT.SET_GAME_CONFIG, {
        cuiPosition: "centre", // LEFT | RIGHT | CENTRE (TODO TO VERIFY LOCATION WE NEED TO USE)
        gameLoadingScreen: true,
        gameName: "Gaming Corps",
      })
    );

    //REGISTER OPTIONS
    const availableOptions = ["MUTE", "ABOUT", "HELP", "PAYTABLE"];
    availableOptions.forEach((option) => {
      this.messagePorts.port1.postMessage(
        this.prepareGcmMsg(LIGHT_AND_WONDER_EVENT.REGISTER_OPTION, {
          optionType: option, // MUTE | ABOUT | HELP | PAYTABLE
        })
      );
    });
  }

  private prepareGcmMsg(event: LIGHT_AND_WONDER_EVENT, data?: any): string {
    const msg = JSON.stringify({
      eventName: event,
      eventData: data ?? {},
    });
    console.warn("::: L&W EVENT SENT", msg);

    return msg;
  }

  private onGcmMsgReceived(msg: any): void {
    msg = JSON.parse(msg.data);

    switch (msg.eventName) {
      case LIGHT_AND_WONDER_RECEIVE_MESSAGE_TYPE.BALANCE_HAS_CHANGED:
        this.observers["balanceChanged"]?.forEach((cb) => cb());
        break;
      case LIGHT_AND_WONDER_RECEIVE_MESSAGE_TYPE.STOP_AUTOPLAY:
        this.observers["stopAutoSpin"]?.forEach((cb) => cb());
        break;
      case LIGHT_AND_WONDER_RECEIVE_MESSAGE_TYPE.RESUME:
        this.observers["unfreeze"]?.forEach((cb) => cb());
        break;
      case LIGHT_AND_WONDER_RECEIVE_MESSAGE_TYPE.OPTION_HAS_CHANGED:
        switch (msg.eventData.option) {
          case "MUTE":
            this.observers["mute"]?.forEach((cb) => cb(msg.eventData.value));
            break;
          case "ABOUT":
            this.observers["toggleAbout"]?.forEach((cb) => cb());
            break;
          case "HELP":
            this.observers["toggleHelp"]?.forEach((cb) => cb());
            break;
          case "PAYTABLE":
            this.observers["togglePaytable"]?.forEach((cb) => cb());
            break;
        }
        break;
    }
  }

  startLoad = () => {};

  setProgressLoad = (loadingPercent: number) => {
    this.messagePorts.port1.postMessage(
      this.prepareGcmMsg(LIGHT_AND_WONDER_EVENT.PROGRESS_UPDATE, {
        percent: loadingPercent,
      })
    );
  };

  endLoad = () => {
    this.updateWin(0);
    this.messagePorts.port1.postMessage(
      this.prepareGcmMsg(LIGHT_AND_WONDER_EVENT.GAME_READY)
    );
  };

  authError = (thirdPartyData?: any) => {
    if (thirdPartyData?.lightandwonder) {
      this.messagePorts.port1.postMessage(
        this.prepareGcmMsg(LIGHT_AND_WONDER_EVENT.HANDLE_MESSAGE_TRIGGER, {
          msgTriggerData: thirdPartyData?.lightandwonder,
        })
      );
    }
  };

  startGame = () => {
    this.messagePorts.port1.postMessage(
      this.prepareGcmMsg(LIGHT_AND_WONDER_EVENT.ROUND_START)
    );

    this.updateWin(0);
  };

  endGame = (thirdPartyData?: any) => {
    this.messagePorts.port1.postMessage(
      this.prepareGcmMsg(LIGHT_AND_WONDER_EVENT.ROUND_END)
    );

    if (thirdPartyData?.lightandwonder) {
      this.messagePorts.port1.postMessage(
        this.prepareGcmMsg(LIGHT_AND_WONDER_EVENT.HANDLE_MESSAGE_TRIGGER, {
          msgTriggerData: thirdPartyData?.lightandwonder,
        })
      );
    }
  };

  updateBalance = (balance: number, bonus_balance: number, thirdPartyData?: any) => {
    this.messagePorts.port1.postMessage(
      this.prepareGcmMsg(LIGHT_AND_WONDER_EVENT.BALANCE_UPDATE, {
        balances: {
          CASH: balance,
          BONUS: bonus_balance,
        },
      })
    );

    if (thirdPartyData?.lightandwonder) {
      this.messagePorts.port1.postMessage(
        this.prepareGcmMsg(LIGHT_AND_WONDER_EVENT.HANDLE_MESSAGE_TRIGGER, {
          msgTriggerData: thirdPartyData?.lightandwonder,
        })
      );
    }
  };

  updateBet = (bet: number) => {
    this.messagePorts.port1.postMessage(
      this.prepareGcmMsg(LIGHT_AND_WONDER_EVENT.STAKE_UPDATE, {
        amount: bet,
      })
    );
  };

  isShowInGameErrors = () => {
    const urlParams = getUrlParams();

    const {  customer } =
      urlParams;
    return customer != "lightandwonder";
  };


  handleError = (error: { title: string; status: number; message: string }) => {
    try{

        const msg = JSON.parse(error.message)

        this.messagePorts.port1.postMessage(this.prepareGcmMsg(LIGHT_AND_WONDER_EVENT.GAME_READY))

        this.messagePorts.port1.postMessage(
            msg.rawErrorMessage === true
                ? this.prepareGcmMsg(LIGHT_AND_WONDER_EVENT.HANDLE_MESSAGE_TRIGGER, {
                    msgTriggerData: msg.third_party_message,
                })
                : this.prepareGcmMsg(LIGHT_AND_WONDER_EVENT.HANDLE_ERROR, {
                    category: msg.category,
                    severity: msg.severity,
                    errorCode: msg.third_party_error_code,
                    message: msg.third_party_message,
                })
        )
    }
    catch(error){
        console.warn('::: L&W ERROR Not formatted correctly ', error)
    }
  };

  updateWin = (win: number) => {
    this.messagePorts.port1.postMessage(
      this.prepareGcmMsg(LIGHT_AND_WONDER_EVENT.PAID_UPDATE, {
        amount: win,
      })
    );
  };

  updateSound = (active: boolean) => {
    this.messagePorts.port1.postMessage(
      this.prepareGcmMsg(LIGHT_AND_WONDER_EVENT.OPTION_HAS_CHANGED, {
        optionType: "MUTE",
        newValue: !active,
        changedFrom: "GAME",
      })
    );
  };

  on = (type: TResponseType, callback: (...args: any) => void) => {
    if (!this.observers[type]) {
      this.observers[type] = [];
    }

    if (!this.observers[type].includes(callback)) {
      this.observers[type].push(callback);
    }
  };

  off = (type: TResponseType, callback: (...args: any) => void) => {
    if (!this.observers[type]) return;

    const observerIndex = this.observers[type].indexOf(callback);

    if (observerIndex > -1) {
      this.observers[type].splice(observerIndex, 1);
    }
  };
}



const lightAndWonderClient = new LightAndWonderClient();

export default lightAndWonderClient;
